/**
 * Pure aggregator for multi-add (BuyingSession) per-item staging results.
 *
 * Day 64 F49: BuyingSession's handleConfirmMultiAdd stages N drafts sequentially
 * via poStaging.addItem(...). Each call now returns { success, productId, error? }
 * (Day 64 Phase 4 signature change). This aggregator computes the user-facing
 * toast outcome from the per-call results.
 *
 * Extracted to a pure function so Phase 8.2 vitest can test the three outcome
 * branches (all-success / all-fail / partial) without rendering BuyingSession's
 * ~3500-line component tree.
 */

import type { AddPOStagingResult } from '../hooks/usePOStaging';

export interface MultiAddOutcome {
  /** Number of successful staging calls. */
  succeeded: number;
  /** Number of failed staging calls (Supabase upsert errors). */
  failed: number;
  /**
   * User-facing toast message. Branch on `failed === 0` to decide between
   * the success ticker (triggerTicker) and an error-tone toast (setToast).
   */
  message: string;
}

export function aggregateMultiAddOutcome(
  results: ReadonlyArray<AddPOStagingResult>
): MultiAddOutcome {
  const succeeded = results.filter((r) => r.success).length;
  const failed = results.length - succeeded;
  let message: string;
  if (failed === 0) {
    message = `${succeeded} SKU${succeeded !== 1 ? 's' : ''} staged`;
  } else if (succeeded === 0) {
    message = `Failed to stage ${failed} SKU${failed !== 1 ? 's' : ''}`;
  } else {
    message = `${succeeded} staged, ${failed} failed`;
  }
  return { succeeded, failed, message };
}
